import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import type { Prayer, PendingPrayer, PrayerType, PrayerSubmissionInput } from '@/types/prayers';

// Collection paths
const getPrayersPath = () => `artifacts/${appId}/public/data/prayers`;
const getPendingPrayersPath = () => `artifacts/${appId}/pending_prayers`;

// ==================== Prayer Submission ====================

/**
 * Submit a new prayer request (goes to pending queue)
 */
export async function submitPrayerRequest(
  input: PrayerSubmissionInput,
  userId: string,
  userName: string
): Promise<string> {
  const pendingRef = collection(db, getPendingPrayersPath());

  const pendingPrayer = {
    name: input.name,
    hebrewName: input.hebrewName || null,
    note: input.note || null,
    type: input.type,
    addedBy: userId,
    addedByName: userName,
    status: 'pending',
    submittedAt: serverTimestamp(),
  };

  const docRef = await addDoc(pendingRef, pendingPrayer);
  return docRef.id;
}

// ==================== Pending Prayer Management ====================

/**
 * Get all pending prayers (for moderators)
 */
export async function getPendingPrayers(): Promise<PendingPrayer[]> {
  const pendingRef = collection(db, getPendingPrayersPath());
  const q = query(pendingRef, where('status', '==', 'pending'), orderBy('submittedAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as PendingPrayer
  );
}

/**
 * Get pending prayers by type
 */
export async function getPendingPrayersByType(type: PrayerType): Promise<PendingPrayer[]> {
  const pendingRef = collection(db, getPendingPrayersPath());
  const q = query(
    pendingRef,
    where('status', '==', 'pending'),
    where('type', '==', type),
    orderBy('submittedAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as PendingPrayer
  );
}

/**
 * Get count of pending prayers
 */
export async function getPendingPrayersCount(): Promise<number> {
  const pendingRef = collection(db, getPendingPrayersPath());
  const q = query(pendingRef, where('status', '==', 'pending'));

  const snapshot = await getDocs(q);
  return snapshot.size;
}

/**
 * Subscribe to pending prayers count (real-time)
 */
export function subscribeToPendingPrayersCount(callback: (count: number) => void): () => void {
  const pendingRef = collection(db, getPendingPrayersPath());
  const q = query(pendingRef, where('status', '==', 'pending'));

  return onSnapshot(q, (snapshot) => {
    callback(snapshot.size);
  });
}

/**
 * Subscribe to pending prayers list (real-time)
 */
export function subscribeToPendingPrayers(
  callback: (prayers: PendingPrayer[]) => void
): () => void {
  const pendingRef = collection(db, getPendingPrayersPath());
  const q = query(pendingRef, where('status', '==', 'pending'), orderBy('submittedAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const prayers = snapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as PendingPrayer
    );
    callback(prayers);
  });
}

// ==================== Prayer Approval/Rejection ====================

/**
 * Approve a pending prayer request
 */
export async function approvePrayer(
  pendingId: string,
  reviewerId: string,
  reviewerName: string
): Promise<string> {
  // Get the pending prayer
  const pendingRef = doc(db, getPendingPrayersPath(), pendingId);
  const pendingSnap = await getDoc(pendingRef);

  if (!pendingSnap.exists()) {
    throw new Error('Pending prayer not found');
  }

  const pendingData = pendingSnap.data() as Omit<PendingPrayer, 'id'>;

  // Create the approved prayer in public collection
  const prayersRef = collection(db, getPrayersPath());
  const approvedPrayer: Omit<Prayer, 'id'> = {
    name: pendingData.name,
    hebrewName: pendingData.hebrewName,
    note: pendingData.note,
    type: pendingData.type,
    addedBy: pendingData.addedBy,
    addedByName: pendingData.addedByName,
    createdAt: pendingData.submittedAt || Timestamp.now(),
  };

  const newPrayerRef = await addDoc(prayersRef, approvedPrayer);

  // Update the pending prayer status
  await updateDoc(pendingRef, {
    status: 'approved',
    reviewedAt: serverTimestamp(),
    reviewedBy: reviewerId,
    reviewedByName: reviewerName,
  });

  return newPrayerRef.id;
}

/**
 * Reject a pending prayer request
 */
export async function rejectPrayer(
  pendingId: string,
  reviewerId: string,
  reviewerName: string,
  reason?: string
): Promise<void> {
  const pendingRef = doc(db, getPendingPrayersPath(), pendingId);
  const pendingSnap = await getDoc(pendingRef);

  if (!pendingSnap.exists()) {
    throw new Error('Pending prayer not found');
  }

  await updateDoc(pendingRef, {
    status: 'rejected',
    reviewedAt: serverTimestamp(),
    reviewedBy: reviewerId,
    reviewedByName: reviewerName,
    rejectionReason: reason || null,
  });
}

/**
 * Delete a pending prayer (permanently remove)
 */
export async function deletePendingPrayer(pendingId: string): Promise<void> {
  const pendingRef = doc(db, getPendingPrayersPath(), pendingId);
  await deleteDoc(pendingRef);
}

// ==================== Approved Prayers ====================

/**
 * Get approved prayers by type
 */
export async function getApprovedPrayers(type: PrayerType): Promise<Prayer[]> {
  const prayersRef = collection(db, getPrayersPath());
  const q = query(prayersRef, where('type', '==', type), orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as Prayer
  );
}

/**
 * Get a single approved prayer by ID
 */
export async function getApprovedPrayer(prayerId: string): Promise<Prayer | null> {
  const prayerRef = doc(db, getPrayersPath(), prayerId);
  const prayerSnap = await getDoc(prayerRef);

  if (!prayerSnap.exists()) {
    return null;
  }

  return { id: prayerSnap.id, ...prayerSnap.data() } as Prayer;
}

/**
 * Subscribe to approved prayers by type (real-time)
 */
export function subscribeToApprovedPrayers(
  type: PrayerType,
  callback: (prayers: Prayer[]) => void
): () => void {
  const prayersRef = collection(db, getPrayersPath());
  const q = query(prayersRef, where('type', '==', type), orderBy('createdAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const prayers = snapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as Prayer
    );
    callback(prayers);
  });
}

/**
 * Delete an approved prayer (moderator action)
 */
export async function deleteApprovedPrayer(prayerId: string): Promise<void> {
  const prayerRef = doc(db, getPrayersPath(), prayerId);
  await deleteDoc(prayerRef);
}

/**
 * Update an approved prayer (moderator action)
 */
export async function updateApprovedPrayer(
  prayerId: string,
  updates: {
    name?: string;
    hebrewName?: string | null;
    note?: string | null;
    type?: 'healing' | 'memory' | 'dedication';
  }
): Promise<void> {
  const prayerRef = doc(db, getPrayersPath(), prayerId);

  // Filter out undefined values (Firebase doesn't accept undefined)
  const cleanedUpdates: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined) {
      cleanedUpdates[key] = value;
    }
  }

  await updateDoc(prayerRef, cleanedUpdates);
}

// ==================== Direct Prayer Creation (Legacy/Admin) ====================

/**
 * Directly create an approved prayer (bypasses pending queue)
 * For use by admins/leaders only
 */
export async function createPrayerDirectly(
  input: PrayerSubmissionInput,
  userId: string,
  userName: string
): Promise<string> {
  const prayersRef = collection(db, getPrayersPath());

  const prayer: Omit<Prayer, 'id'> = {
    name: input.name,
    hebrewName: input.hebrewName,
    note: input.note,
    type: input.type,
    addedBy: userId,
    addedByName: userName,
    createdAt: serverTimestamp() as unknown as Timestamp,
  };

  const docRef = await addDoc(prayersRef, prayer);
  return docRef.id;
}

// ==================== All Prayers (Cross-Type) ====================

/**
 * Get all approved prayers across all types
 */
export async function getAllApprovedPrayers(): Promise<Prayer[]> {
  const prayersRef = collection(db, getPrayersPath());
  const q = query(prayersRef, orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (doc) =>
      ({
        id: doc.id,
        ...doc.data(),
      }) as Prayer
  );
}

/**
 * Subscribe to all approved prayers (real-time, all types)
 */
export function subscribeToAllApprovedPrayers(callback: (prayers: Prayer[]) => void): () => void {
  const prayersRef = collection(db, getPrayersPath());
  const q = query(prayersRef, orderBy('createdAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const prayers = snapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as Prayer
    );
    callback(prayers);
  });
}

// ==================== Pinned Prayers ====================

/**
 * Pin a prayer (feature it in the summary spotlight).
 * Enforces a 1-per-type limit: if another prayer of the same type is already pinned,
 * it will be unpinned first.
 */
export async function pinPrayer(prayerId: string, userId: string): Promise<void> {
  const prayerRef = doc(db, getPrayersPath(), prayerId);
  const prayerSnap = await getDoc(prayerRef);

  if (!prayerSnap.exists()) {
    throw new Error('Prayer not found');
  }

  const prayerData = prayerSnap.data();
  const prayerType = prayerData.type as string;

  // Check for existing pinned prayer of same type and unpin it
  const prayersRef = collection(db, getPrayersPath());
  const pinnedQuery = query(
    prayersRef,
    where('type', '==', prayerType),
    where('pinned', '==', true)
  );
  const pinnedSnap = await getDocs(pinnedQuery);

  // Unpin any existing pinned prayer of the same type
  const unpinPromises = pinnedSnap.docs
    .filter((d) => d.id !== prayerId)
    .map((d) =>
      updateDoc(doc(db, getPrayersPath(), d.id), {
        pinned: false,
        pinnedAt: deleteField(),
        pinnedBy: deleteField(),
      })
    );
  await Promise.all(unpinPromises);

  // Pin the requested prayer
  await updateDoc(prayerRef, {
    pinned: true,
    pinnedAt: serverTimestamp(),
    pinnedBy: userId,
  });
}

/**
 * Unpin a prayer (remove from spotlight)
 */
export async function unpinPrayer(prayerId: string): Promise<void> {
  const prayerRef = doc(db, getPrayersPath(), prayerId);
  await updateDoc(prayerRef, {
    pinned: false,
    pinnedAt: deleteField(),
    pinnedBy: deleteField(),
  });
}

/**
 * Subscribe to pinned prayers (real-time)
 */
export function subscribeToPinnedPrayers(callback: (prayers: Prayer[]) => void): () => void {
  const prayersRef = collection(db, getPrayersPath());
  const q = query(prayersRef, where('pinned', '==', true));

  return onSnapshot(q, (snapshot) => {
    const prayers = snapshot.docs.map(
      (d) =>
        ({
          id: d.id,
          ...d.data(),
        }) as Prayer
    );
    callback(prayers);
  });
}

// ==================== Unapprove (Return to Queue) ====================

/**
 * Move an approved prayer back into the pending approval queue.
 * Creates a new pending_prayers document and deletes the approved prayer.
 */
export async function unapprovePrayer(
  prayerId: string,
  reviewerId: string,
  reviewerName: string
): Promise<string> {
  const prayerRef = doc(db, getPrayersPath(), prayerId);
  const prayerSnap = await getDoc(prayerRef);

  if (!prayerSnap.exists()) {
    throw new Error('Prayer not found');
  }

  const prayerData = prayerSnap.data() as Omit<Prayer, 'id'>;

  // If pinned, unpin before removing
  if (prayerData.pinned) {
    await updateDoc(prayerRef, {
      pinned: false,
      pinnedAt: deleteField(),
      pinnedBy: deleteField(),
    });
  }

  // Create a new pending prayer from the approved data
  const pendingRef = collection(db, getPendingPrayersPath());
  const pendingPrayer = {
    name: prayerData.name,
    hebrewName: prayerData.hebrewName || null,
    note: prayerData.note || null,
    type: prayerData.type,
    addedBy: prayerData.addedBy,
    addedByName: prayerData.addedByName || null,
    status: 'pending',
    submittedAt: serverTimestamp(),
    reviewedAt: serverTimestamp(),
    reviewedBy: reviewerId,
    reviewedByName: reviewerName,
  };

  const newPendingRef = await addDoc(pendingRef, pendingPrayer);

  // Delete the approved prayer
  await deleteDoc(prayerRef);

  return newPendingRef.id;
}

// ==================== Donation Template Linking ====================

/**
 * Link multiple prayers to a donation template
 */
export async function linkPrayersToDonationTemplate(
  prayerIds: string[],
  templateId: string,
  templateName: string,
  linkedBy: string
): Promise<void> {
  const updates = {
    donationTemplateId: templateId,
    donationTemplateName: templateName,
    donationLinkedAt: serverTimestamp(),
    donationLinkedBy: linkedBy,
  };

  // Update each prayer
  const updatePromises = prayerIds.map((prayerId) => {
    const prayerRef = doc(db, getPrayersPath(), prayerId);
    return updateDoc(prayerRef, updates);
  });

  await Promise.all(updatePromises);
}

/**
 * Remove donation template from multiple prayers
 */
export async function unlinkPrayersFromDonationTemplate(prayerIds: string[]): Promise<void> {
  const updates = {
    donationTemplateId: null,
    donationTemplateName: null,
    donationLinkedAt: null,
    donationLinkedBy: null,
  };

  // Update each prayer
  const updatePromises = prayerIds.map((prayerId) => {
    const prayerRef = doc(db, getPrayersPath(), prayerId);
    return updateDoc(prayerRef, updates);
  });

  await Promise.all(updatePromises);
}
